<template>
  <div
    class="course-card"
    :class="{ 'course-card-compact': compact }"
    @click="$emit('view-details', course)"
  >
    <!-- Course Thumbnail -->
    <div class="course-thumbnail">
      <img
        v-if="course.thumbnail && !thumbnailFailed"
        :src="course.thumbnail"
        :alt="course.title"
        class="thumbnail-image"
        @error="thumbnailFailed = true"
      />
      <div v-else class="thumbnail-placeholder">
        <i class="fas fa-graduation-cap placeholder-icon" aria-hidden="true"></i>
      </div>
      
      <!-- Content origin badge (mixed-library catalogs) -->
      <div v-if="course.origin_badge_label" class="origin-badge" :title="course.origin_organization_name">
        {{ course.origin_badge_label }}
      </div>
      
      <!-- Course Type Badge -->
      <div class="course-type-badge" :class="courseTypeClass">
        {{ courseTypeLabel }}
      </div>
      
      <!-- Certificate Course Badge - ALWAYS visible for courses with quiz/certification -->
      <div v-if="hasCertification" class="certificate-badge">
        <i class="fas fa-certificate"></i>
        Certificate
      </div>
      
      <!-- Live Event Indicator -->
      <div v-if="isLiveEvent && isUpcoming" class="live-indicator">
        <span class="live-dot"></span>
        LIVE
      </div>
    </div>

    <!-- Course Content -->
    <div class="course-content">
      <!-- Course Header -->
      <div class="course-header">
        <h3 class="course-title">{{ course.title }}</h3>
        <div v-if="!compact" class="course-meta">
          <span v-if="course.category_name" class="category">
            {{ course.category_name }}
          </span>
          <span v-if="course.difficulty_level" class="difficulty" :class="difficultyClass">
            {{ course.difficulty_level }}
          </span>
        </div>
        <div v-else class="compact-meta">
          <span v-if="course.category_name" class="category">{{ course.category_name }}</span>
          <span v-if="course.credit_hours" class="compact-ce">
            {{ formatCompactCE(course.credit_hours) }}
          </span>
        </div>
      </div>

      <template v-if="!compact">
        <!-- What You'll Learn (Learning Objectives) -->
        <div v-if="course.learning_objectives && course.learning_objectives.length > 0" class="learning-objectives">
          <ul class="objectives-list">
            <li v-for="(objective, index) in displayedObjectives" :key="index" class="objective-item">
              {{ objective }}
            </li>
          </ul>
          <span v-if="course.learning_objectives.length > 3" class="more-objectives">
            +{{ course.learning_objectives.length - 3 }} more learning objectives
          </span>
        </div>
        
        <!-- Fallback to description if no learning objectives -->
        <p v-else class="course-description">
          {{ course.short_description || course.description }}
        </p>

        <!-- Course Details -->
        <div class="course-details">
          <!-- Instructor — prefer structured FK list (Phase 1b), fall back to legacy text -->
          <div v-if="instructorLabel" class="detail-item">
            <i class="detail-icon">👨‍🏫</i>
            <span>{{ instructorLabel }}</span>
          </div>

          <!-- CE Credits with RACE -->
          <div v-if="course.credit_hours" class="detail-item">
            <i class="detail-icon">🏆</i>
            <span>{{ formatCECredits(course.credit_hours) }}</span>
          </div>

          <!-- Scheduled Time (for live events) -->
          <div v-if="isLiveEvent && course.scheduled_start" class="detail-item">
            <i class="detail-icon">📅</i>
            <span>{{ formatScheduledTime(course.scheduled_start) }}</span>
          </div>
        </div>

        <!-- Rating and Enrollment -->
        <div class="course-stats">
          <div v-if="course.average_rating" class="rating">
            <div class="stars">
              <span v-for="star in 5" :key="star" class="star" :class="{ filled: star <= Math.round(course.average_rating) }">
                ★
              </span>
            </div>
            <span class="rating-text">{{ course.average_rating }} ({{ course.review_count }})</span>
          </div>
          
          <div class="enrollment-count">
            {{ course.enrollment_count }} enrolled
          </div>
        </div>

        <!-- Tags -->
        <div v-if="course.tags && course.tags.length > 0" class="course-tags">
          <span v-for="tag in course.tags.slice(0, 3)" :key="tag" class="tag">
            {{ tag }}
          </span>
          <span v-if="course.tags.length > 3" class="tag-more">
            +{{ course.tags.length - 3 }} more
          </span>
        </div>

        <!-- Certification Requirements Preview - ALWAYS visible -->
        <div v-if="hasCertification" class="certification-preview">
          <div class="cert-header">
            <i class="fas fa-award"></i>
            <span>Earn Certificate</span>
          </div>
          <div class="cert-requirements">
            <span class="req-item">
              <i class="fas fa-play-circle"></i>
              {{ course.video_count || 'Multiple' }} videos
            </span>
            <span class="req-item">
              <i class="fas fa-clipboard-check"></i>
              Quiz ({{ course.quiz_passing_score || 70 }}% to pass)
            </span>
          </div>
        </div>
      </template>
    </div>

    <!-- Course Footer -->
    <div class="course-footer">
      <!-- Price -->
      <div class="price-section">
        <span v-if="course.is_free" class="price free">FREE</span>
        <div v-else class="price-container">
          <!-- Free course benefit available for this member -->
          <template v-if="freeCourseAvailable">
            <span class="price original-price">${{ course.price }}</span>
            <span class="price member-price free-benefit-price">FREE</span>
            <span v-if="!compact" class="member-discount-badge free-badge">
              <i class="fas fa-gift"></i>
              Free Course Benefit
            </span>
          </template>
          <!-- Paid but with member discount -->
          <template v-else-if="isMember">
            <span class="price original-price">${{ course.price }}</span>
            <span class="price member-price">${{ memberPrice }}</span>
            <span v-if="!compact" class="member-discount-badge">
              <i class="fas fa-check-circle"></i>
              {{ discountPercentage }}% Member Discount
            </span>
          </template>
          <!-- Non-member -->
          <template v-else>
            <span class="price">${{ course.price }}</span>
            <span v-if="!compact" class="member-discount-badge non-member">Members 40% off + 1 free/year</span>
          </template>
        </div>
      </div>

      <!-- Action Button -->
      <button
        v-if="!course.is_enrolled"
        @click.stop="$emit('enroll', course)"
        class="enroll-btn"
        :class="{ 'btn-primary': !isLiveEvent || isUpcoming, 'btn-disabled': isLiveEvent && !isUpcoming }"
        :disabled="isLiveEvent && !isUpcoming"
      >
        {{ compact ? 'View' : enrollButtonText }}
      </button>
      
      <div v-else class="enrolled-status">
        <i class="status-icon">✓</i>
        <span>Enrolled</span>
      </div>
    </div>

    <!-- Progress Bar (for enrolled courses) -->
    <div v-if="!compact && course.is_enrolled && course.user_progress" class="progress-section">
      <div class="progress-bar">
        <div 
          class="progress-fill" 
          :style="{ width: course.user_progress.progress_percentage + '%' }"
        ></div>
      </div>
      <span class="progress-text">
        {{ Math.round(course.user_progress.progress_percentage) }}% Complete
      </span>
    </div>
  </div>
</template>

<script>
import { computed, ref, watch } from 'vue'
import auth from '@/services/auth'

export default {
  name: 'CourseCard',
  props: {
    course: {
      type: Object,
      required: true
    },
    /** Dense layout for inline consult / discovery surfaces */
    compact: {
      type: Boolean,
      default: false
    }
  },
  emits: ['enroll', 'view-details'],
  setup(props) {
    const thumbnailFailed = ref(false)
    watch(() => props.course?.thumbnail, () => {
      thumbnailFailed.value = false
    })

    // Member pricing
    const isMember = computed(() => auth.isVerifiedMember())
    const discountPercentage = computed(() => auth.getMemberDiscountPercentage())
    const freeCourseAvailable = computed(() => auth.hasAnnualFreeCourseAvailable())
    const memberPrice = computed(() => {
      const pricing = auth.calculateMemberPrice(props.course.price)
      return pricing.finalPrice.toFixed(2)
    })
    // Display only first 3 learning objectives on the card
    const displayedObjectives = computed(() => {
      if (!props.course.learning_objectives) return []
      return props.course.learning_objectives.slice(0, 3)
    })

    // Phase 1b — prefer the structured FK list, fall back to legacy text.
    const instructorLabel = computed(() => {
      const list = Array.isArray(props.course.instructors) ? props.course.instructors : []
      if (list.length) return list[0].full_name
      return props.course.instructor_name || ''
    })

    // Check if course offers certification (has quiz questions OR credit hours)
    const hasCertification = computed(() => {
      return (props.course.quiz_questions && props.course.quiz_questions.length > 0) ||
             props.course.credit_hours ||
             props.course.certification_type
    })
    
    const courseTypeClass = computed(() => {
      const typeMap = {
        'LIVE_EVENT': 'live-event',
        'ON_DEMAND': 'on-demand',
        'WEBINAR': 'webinar',
        'WORKSHOP': 'workshop',
        'CERTIFICATION': 'certification'
      }
      return typeMap[props.course.course_type] || 'default'
    })
    
    const courseTypeLabel = computed(() => {
      const labelMap = {
        'LIVE_EVENT': 'Live Event',
        'ON_DEMAND': 'On-Demand',
        'WEBINAR': 'Webinar',
        'WORKSHOP': 'Workshop',
        'CERTIFICATION': 'Certification'
      }
      return labelMap[props.course.course_type] || props.course.course_type
    })
    
    const difficultyClass = computed(() => {
      const difficultyMap = {
        'BEGINNER': 'beginner',
        'INTERMEDIATE': 'intermediate',
        'ADVANCED': 'advanced',
        'EXPERT': 'expert'
      }
      return difficultyMap[props.course.difficulty_level] || 'default'
    })
    
    const isLiveEvent = computed(() => {
      return props.course.course_type === 'LIVE_EVENT'
    })
    
    const isUpcoming = computed(() => {
      if (!props.course.scheduled_start) return false
      return new Date(props.course.scheduled_start) > new Date()
    })
    
    const enrollButtonText = computed(() => {
      if (isLiveEvent.value) {
        return isUpcoming.value ? 'Register' : 'Event Ended'
      }
      return 'Enroll Now'
    })
    
    const formatScheduledTime = (dateString) => {
      const date = new Date(dateString)
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        timeZoneName: 'short'
      })
    }
    
    const formatCECredits = (hours) => {
      if (!hours) return ''
      // Convert to number and handle non-numeric values
      const numericHours = parseFloat(hours)
      if (isNaN(numericHours)) return ''
      const formattedHours = numericHours.toFixed(2)
      return `${formattedHours} hr. CE Credit - RACE`
    }

    const formatCompactCE = (hours) => {
      const numericHours = parseFloat(hours)
      if (isNaN(numericHours)) return ''
      return `${numericHours.toFixed(2)} CE hrs`
    }
    
    return {
      thumbnailFailed,
      displayedObjectives,
      instructorLabel,
      hasCertification,
      courseTypeClass,
      courseTypeLabel,
      difficultyClass,
      isLiveEvent,
      isUpcoming,
      enrollButtonText,
      formatScheduledTime,
      formatCECredits,
      formatCompactCE,
      // Member pricing
      isMember,
      discountPercentage,
      freeCourseAvailable,
      memberPrice
    }
  }
}
</script>

<style scoped>
.course-card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  transition: all 0.3s ease;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  height: 100%;
}

.course-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
}

.course-thumbnail {
  position: relative;
  width: 100%;
  height: 200px;
  overflow: hidden;
}

.thumbnail-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.thumbnail-placeholder {
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #0f766e 0%, #134e4a 100%);
  display: flex;
  align-items: center;
  justify-content: center;
}

.placeholder-icon {
  font-size: 2.5rem;
  color: white;
  opacity: 0.85;
}

.course-type-badge {
  position: absolute;
  top: 12px;
  left: 12px;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.course-type-badge.live-event {
  background: #ef4444;
  color: white;
}

.course-type-badge.on-demand {
  background: #10b981;
  color: white;
}

.course-type-badge.webinar {
  background: #f59e0b;
  color: white;
}

.course-type-badge.workshop {
  background: #8b5cf6;
  color: white;
}

.course-type-badge.certification {
  background: #06b6d4;
  color: white;
}

.live-indicator {
  position: absolute;
  top: 12px;
  right: 12px;
  background: rgba(239, 68, 68, 0.9);
  color: white;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.live-dot {
  width: 6px;
  height: 6px;
  background: white;
  border-radius: 50%;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

.origin-badge {
  position: absolute;
  bottom: 12px;
  left: 12px;
  background: rgba(15, 31, 61, 0.85);
  color: white;
  padding: 0.25rem 0.65rem;
  border-radius: 6px;
  font-size: 0.7rem;
  font-weight: 600;
  max-width: calc(100% - 24px);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Certificate Badge - prominent golden badge */
.certificate-badge {
  position: absolute;
  top: 12px;
  right: 12px;
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  color: white;
  padding: 0.35rem 0.85rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 0.375rem;
  box-shadow: 0 2px 8px rgba(245, 158, 11, 0.4);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.certificate-badge i {
  font-size: 0.9rem;
}

/* Certification Requirements Preview */
.certification-preview {
  margin-top: 1rem;
  padding: 0.875rem;
  background: linear-gradient(135deg, rgba(245, 158, 11, 0.08) 0%, rgba(16, 185, 129, 0.08) 100%);
  border: 1px solid rgba(245, 158, 11, 0.2);
  border-radius: 8px;
}

.cert-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  color: #d97706;
  font-size: 0.875rem;
  margin-bottom: 0.5rem;
}

.cert-header i {
  font-size: 1rem;
}

.cert-requirements {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.req-item {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.8rem;
  color: #4b5563;
}

.req-item i {
  color: #10b981;
  font-size: 0.85rem;
}

.course-content {
  padding: 1.5rem;
  flex: 1;
  display: flex;
  flex-direction: column;
}

.course-header {
  margin-bottom: 1rem;
}

.course-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 0.5rem;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.course-meta {
  display: flex;
  gap: 0.75rem;
  align-items: center;
}

.category {
  background: #f3f4f6;
  color: #374151;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
}

.difficulty {
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
  text-transform: capitalize;
}

.difficulty.beginner {
  background: #dcfce7;
  color: #166534;
}

.difficulty.intermediate {
  background: #fef3c7;
  color: #92400e;
}

.difficulty.advanced {
  background: #fee2e2;
  color: #991b1b;
}

.difficulty.expert {
  background: #f3e8ff;
  color: #6b21a8;
}

.learning-objectives {
  margin-bottom: 1rem;
}

.objectives-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.objective-item {
  color: #374151;
  font-size: 0.875rem;
  line-height: 1.6;
  margin-bottom: 0.5rem;
  padding-left: 1.5rem;
  position: relative;
}

.objective-item::before {
  content: "✓";
  position: absolute;
  left: 0;
  color: #10b981;
  font-weight: 600;
  font-size: 1rem;
}

.more-objectives {
  display: block;
  color: #6b7280;
  font-size: 0.75rem;
  font-style: italic;
  margin-top: 0.25rem;
  padding-left: 1.5rem;
}

.course-description {
  color: #6b7280;
  font-size: 0.875rem;
  line-height: 1.5;
  margin-bottom: 1rem;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.course-details {
  margin-bottom: 1rem;
}

.detail-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
  color: #6b7280;
}

.detail-icon {
  font-size: 1rem;
}

.course-stats {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.rating {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.stars {
  display: flex;
  gap: 0.125rem;
}

.star {
  color: #d1d5db;
  font-size: 1rem;
}

.star.filled {
  color: #fbbf24;
}

.rating-text {
  font-size: 0.875rem;
  color: #6b7280;
}

.enrollment-count {
  font-size: 0.875rem;
  color: #6b7280;
}

.course-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.tag {
  background: #f3f4f6;
  color: #374151;
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  font-size: 0.75rem;
}

.tag-more {
  color: #6b7280;
  font-size: 0.75rem;
  font-style: italic;
}

.course-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem 1.5rem;
  border-top: 1px solid #f3f4f6;
  margin-top: auto;
}

.price-section {
  display: flex;
  align-items: center;
}

.price-container {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.price {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1f2937;
}

.price.free {
  color: #10b981;
}

.member-discount-badge {
  font-size: 0.75rem;
  font-weight: 600;
  color: #10b981;
  background: #d1fae5;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
}

.member-discount-badge i {
  font-size: 0.7rem;
}

.member-discount-badge.non-member {
  color: #6b7280;
  background: #f3f4f6;
}

.member-discount-badge.free-badge {
  color: #14532d;
  background: #dcfce7;
  border: 1px solid #86efac;
}

.price.free-benefit-price {
  color: #16a34a;
  font-size: 1.25rem;
  font-weight: 700;
}

/* Member pricing styles */
.price.original-price {
  font-size: 0.9rem;
  color: #9ca3af;
  text-decoration: line-through;
  font-weight: 400;
}

.price.member-price {
  font-size: 1.25rem;
  font-weight: 700;
  color: #10b981;
}

.enroll-btn {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-primary {
  background: #3b82f6;
  color: white;
}

.btn-primary:hover {
  background: #2563eb;
}

.btn-disabled {
  background: #9ca3af;
  color: white;
  cursor: not-allowed;
}

.enrolled-status {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #10b981;
  font-weight: 500;
}

.status-icon {
  background: #10b981;
  color: white;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
}

.progress-section {
  padding: 0 1.5rem 1.5rem;
}

.progress-bar {
  width: 100%;
  height: 6px;
  background: #f3f4f6;
  border-radius: 3px;
  overflow: hidden;
  margin-bottom: 0.5rem;
}

.progress-fill {
  height: 100%;
  background: #10b981;
  transition: width 0.3s ease;
}

.progress-text {
  font-size: 0.875rem;
  color: #6b7280;
  font-weight: 500;
}

/* Compact variant — consult / discovery inline cards */
.course-card-compact {
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
}

.course-card-compact:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.12);
}

.course-card-compact .course-thumbnail {
  height: 120px;
  background: #f1f5f9;
}

.course-card-compact .course-type-badge,
.course-card-compact .certificate-badge {
  top: 8px;
  font-size: 0.65rem;
  padding: 0.2rem 0.5rem;
}

.course-card-compact .course-type-badge {
  left: 8px;
}

.course-card-compact .certificate-badge {
  right: 8px;
  gap: 0.25rem;
}

.course-card-compact .course-content {
  padding: 0.75rem 0.875rem 0.25rem;
}

.course-card-compact .course-header {
  margin-bottom: 0;
}

.course-card-compact .course-title {
  font-size: 0.875rem;
  font-weight: 600;
  margin-bottom: 0.35rem;
  -webkit-line-clamp: 2;
  line-height: 1.3;
  min-height: 2.3em;
}

.compact-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  align-items: center;
}

.compact-ce {
  font-size: 0.7rem;
  font-weight: 600;
  color: #0f766e;
  background: #ecfdf5;
  padding: 0.15rem 0.45rem;
  border-radius: 4px;
}

.course-card-compact .course-footer {
  padding: 0.625rem 0.875rem 0.875rem;
  border-top: none;
  gap: 0.5rem;
}

.course-card-compact .price {
  font-size: 0.95rem;
}

.course-card-compact .enroll-btn {
  padding: 0.4rem 0.85rem;
  font-size: 0.75rem;
  border-radius: 6px;
}

@media (max-width: 768px) {
  .course-footer {
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
  }
  
  .enroll-btn {
    width: 100%;
  }

  .course-card-compact .course-footer {
    flex-direction: row;
    align-items: center;
  }

  .course-card-compact .enroll-btn {
    width: auto;
  }
}
</style>
